import { movingPack } from './moving';
import { cleaningPack } from './cleaning';
import { validatePack, type PackContract, type PackId } from './types';

/**
 * 정비·렌탈은 앵커만 등재한 아키텍처 슬롯이다. 체크리스트가 비어 있고
 * `implemented: false`라 UI에 노출되지 않는다. 게이트는 오너 실기(실물 문서 확보)다.
 */
const repairSlot: PackContract = {
  packId: 'repair',
  name: '자동차 정비',
  documentName: '점검·정비 견적서·명세서',
  anchor: {
    doc: '자동차관리법',
    article: '제58조 제5항 · 시행규칙 제133조',
    force: 'mandatory',
    appliesTo: '자동차관리법상 자동차정비업자의 점검·정비 견적서·명세서 발급',
    url: 'https://law.go.kr/lsLawLinkInfo.do?chrClsCd=010202&lsJoLnkSeq=1000583395',
    checkedAt: '2026-08-07',
  },
  providerBranch: false,
  groups: ['labor', 'parts', 'price', 'warranty', 'dispute'],
  serviceDayLabels: ['입고 전', '작업 중', '출고 당일'],
  checklist: [],
  implemented: false,
  blockedBy: 'owner_manual: PII 제거된 실제 정비 명세서 20쌍과 기기내 OCR 스파이크가 있어야 항목을 확정한다. 근거 docs/02-market/visit-contract-platform-phase1.md §6',
};

const rentalSlot: PackContract = {
  packId: 'rental',
  name: '가전 렌탈 해지',
  documentName: '렌탈 계약서·해지 청구서',
  anchor: {
    doc: '소비자분쟁해결기준',
    article: '가전제품 렌탈 — 의무사용기간 초과 시 잔여 월 임대료 10% 기준',
    force: 'advisory',
    appliesTo: '분쟁 발생 시 합의·권고의 기준. 사업자 약관이 이와 다를 수 있다',
    url: 'https://www.easylaw.go.kr/CSP/CnpClsMain.laf?popMenu=ov&csmSeq=669&ccfNo=3&cciNo=2&cnpClsNo=1',
    checkedAt: '2026-08-07',
  },
  providerBranch: false,
  groups: ['contract', 'price', 'penalty', 'return', 'dispute'],
  serviceDayLabels: ['해지 통보 전', '수거일', '정산 후'],
  checklist: [],
  implemented: false,
  blockedBy: 'owner_manual: 실제 렌탈 계약서 30건의 수식 추출률과 사업자 청구서 10건이 있어야 3열 대조를 확정한다. 근거 docs/02-market/visit-contract-platform-phase1.md §6',
};

const all: Record<PackId, PackContract> = {
  moving: movingPack,
  cleaning: cleaningPack,
  repair: repairSlot,
  rental: rentalSlot,
};

const contractErrors = Object.values(all).flatMap(validatePack);
if (contractErrors.length > 0) {
  throw new Error(`팩 계약이 자기 선언과 어긋납니다:\n${contractErrors.join('\n')}`);
}

export const packRegistry = all;

export function getPack(packId: PackId): PackContract {
  return all[packId];
}

/** UI가 고를 수 있는 팩. 미구현 슬롯은 절대 나오지 않는다. */
export function listAvailablePacks(): PackContract[] {
  return Object.values(all).filter((pack) => pack.implemented);
}

/** 등재됐지만 아직 열지 않은 팩. 로드맵 표기와 감사용이며 선택지로 쓰지 않는다. */
export function listPendingPacks(): PackContract[] {
  return Object.values(all).filter((pack) => !pack.implemented);
}

export function findItem(packId: PackId, itemId: string) {
  return all[packId].checklist.find((item) => item.id === itemId);
}

/** 항목 ID만으로 소속 팩을 되짚는다. 저장된 프로젝트의 팩 추론에 쓴다. */
export function packIdForItem(itemId: string): PackId | null {
  for (const pack of Object.values(all)) {
    if (pack.checklist.some((item) => item.id === itemId)) return pack.packId;
  }
  return null;
}

export * from './types';
export { movingPack, cleaningPack };
